/**
 * EmailChangePage - Step 2 of email change flow (#1335)
 *
 * User lands here after clicking the verification link sent to their current email.
 * They enter their new email address, which triggers a verification to the new address.
 *
 * Route: /settings/email-change?token=xxx
 */

import { useState, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api/auth';
import { ApiError } from '../../lib/api';
import { TopBar, Button, Input } from '../../components';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

export default function EmailChangePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newEmail, setNewEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Change Email" onBack={() => navigate('/settings/profile')} />
        <div className="flex-1 flex items-center justify-center px-4 pt-14">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-oro-error mx-auto" />
            <p className="text-text-primary font-medium">Invalid or missing token</p>
            <p className="text-text-secondary text-sm">This link may have expired. Please request a new one from your profile settings.</p>
            <Button variant="secondary" onClick={() => navigate('/settings/profile')}>
              Back to Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await authApi.verifyEmailChange(token, newEmail.trim());
      setSuccess(true);
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to process email change. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Change Email" onBack={() => navigate('/settings/profile')} />
        <div className="flex-1 flex items-center justify-center px-4 pt-14">
          <div className="text-center space-y-4 max-w-sm">
            <CheckCircle className="w-12 h-12 text-oro-success mx-auto" />
            <p className="text-text-primary font-medium">Email change requires additional verification</p>
            <p className="text-text-secondary text-sm">
              We sent a verification link to <span className="text-text-primary font-medium">{newEmail}</span>.
              Click the link in your email to continue.
            </p>
            <p className="text-text-muted text-xs">The link expires in 15 minutes.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Change Email" onBack={() => navigate('/settings/profile')} />
      <div className="flex-1 pt-14 px-4">
        <div className="max-w-sm mx-auto pt-8 space-y-6">
          <div className="text-center space-y-2">
            <Mail className="w-10 h-10 text-accent mx-auto" />
            <h2 className="text-lg font-semibold text-text-primary">Enter Your New Email</h2>
            <p className="text-sm text-text-secondary">
              Your identity has been verified. Enter the new email address you'd like to use.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                New Email Address
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="your-new-email@example.com"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-oro-error-soft border border-oro-error-border flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-oro-error shrink-0 mt-0.5" />
                <p className="text-sm text-oro-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={isSubmitting}
              disabled={isSubmitting || !newEmail.trim()}
            >
              Send Verification
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
